/**
 * React context for white-label theme state.
 *
 * Provides the resolved branding configuration and loading state
 * to all descendant components. Consumed via the {@link useTheme} hook.
 *
 * @module shared/theme/ThemeContext
 */

import { createContext, useContext } from 'react';
import type { BrandingConfig } from './whitelabel-api';
import { DEFAULT_BRANDING_CONFIG } from './whitelabel-api';

/** Shape of the theme context value. */
export interface ThemeContextValue {
  /** Resolved branding configuration for the current domain. */
  branding: BrandingConfig;
  /** True while the initial branding fetch is in progress. */
  loading: boolean;
  /** Error message if the branding fetch failed, null otherwise. */
  error: string | null;
}

export const ThemeContext = createContext<ThemeContextValue>({
  branding: DEFAULT_BRANDING_CONFIG,
  loading: false,
  error: null,
});

/**
 * Hook to access the resolved white-label theme configuration.
 *
 * Must be called within a component tree wrapped by {@link ThemeProvider}.
 * Returns the IPA default branding if used outside a ThemeProvider.
 *
 * @returns The current theme context value.
 */
export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
